import React, { useCallback, useMemo, useState } from 'react';
// import { useSignals } from '@preact/signals-react/runtime';
import { Marker, Rectangle, Polygon, Popup, Pane, useMapEvents } from 'react-leaflet'
import { divIcon, icon } from 'leaflet';
import { useWindowSize } from 'react-use';
import jsonData from '../../signals/jsonData';
import appState from '../../signals/appState';
import markerFav from './assets/marker-fav.svg';
import markerSearch from './assets/marker-search.svg';
import useRecord from '../../hooks/useRecord';
import { exhibitorKeyOf, findExhibitorByKey } from '../../tools/boothExhibitors';
import './leaflet.css';

const FeatureWithText = ({ map, shape, positions, bounds, exhibitors, colors, isPrint, id }) => {
	// 一個圖形可以掛多家。exhibitors 是這個圖形上的全部廠商；
	// 只掛一家時 exhibitor 就是那一家，畫面跟以前完全一樣。
	const exhibitor = exhibitors?.[0];
	// 同一個號碼底下多家時號碼會重複，去重——標題只印一次、Pane 名稱也只能有一個。
	const boothNumbers = useMemo(() => [...new Set((exhibitors || []).map((候選) => 候選?.['攤位號碼']).filter(Boolean))], [exhibitors]);
	const multi = (exhibitors?.length || 0) > 1;
	// useSignals();

	const { currentLanguage, exhibitorInfo } = jsonData.value;
	const { selectedBooth, selectedExhibitor } = appState.value;
	const [rectCurrent, setRectCurrent] = useState(null);

	const onRectLoad = useCallback((map) => {
		setRectCurrent(map);
	}, []);

	const { makeRecord } = useRecord();
	const recordExhibitorClicked = useCallback((booth) => {
		makeRecord({
			Action: 'T',
			Booth: booth,
		});
	}, [makeRecord]);

	const { width } = useWindowSize();

	const minZoom = useMemo(() => {
		if (width <= 1440) return 1;
		return 2;
	}, [width]);

	const [currentZoom, setCurrentZoom] = useState(minZoom);

	const mapEvents = useMapEvents({
		zoomend: () => {
			setCurrentZoom(mapEvents.getZoom());
		},
	});

	const exhibitorName = useMemo(() => {
		return exhibitor?.['廠商簡稱'] || '';
	}, [exhibitor]);

	const featureWidthPixels = useMemo(() => {
		if (map) {
			const pixelPosition = map.project([bounds[0][0], bounds[0][1]], currentZoom);
			const pixelPosition2 = map.project([bounds[1][0], bounds[1][1]], currentZoom);
			return `${(pixelPosition2.x - pixelPosition.x).toString().replace('-', '')}px`;
		}
		return '0';
	}, [bounds, currentZoom, map]);

	const featureHeightPixels = useMemo(() => {
		if (map) {
			const pixelPosition = map.project([bounds[0][0], bounds[0][1]], currentZoom);
			const pixelPosition2 = map.project([bounds[1][0], bounds[1][1]], currentZoom);
			return `${(pixelPosition2.y - pixelPosition.y).toString().replace('-', '')}px`;
		}
		return '0';
	}, [bounds, currentZoom, map]);

	const getTextBits = useCallback((name = exhibitorName) => {
		let charactersPerLine = 8;

		const textBits = [];
		const exhibitorNameLines = name.split(' ');
		let currentLine = '';
		exhibitorNameLines.forEach((word) => {
			if (currentLine.length + word.length > charactersPerLine) {
				textBits.push(currentLine);
				currentLine = '';
			}
			currentLine += ` ${word}`;
		});
		textBits.push(currentLine);

		textBits.forEach((text, index) => {
			textBits[index] = text.trim();
		});

		return textBits;
	}, [exhibitorName]);

	// 標籤上的名稱要畫幾行。掛一家時跟以前一樣；兩三家逐家一行；
	// 超過三家寫第一家加「等 N 家」——一個攤位大小的格子塞不下四行還看得清楚的字。
	const nameLines = useMemo(() => {
		const names = (exhibitors || []).map((候選) => 候選?.['廠商簡稱'] || 候選?.['廠商名稱'] || '');
		const zh = currentLanguage.code.includes('zh');
		if (names.length <= 1) return zh ? [exhibitorName] : getTextBits(exhibitorName);
		if (names.length <= 3) return zh ? names : names.flatMap((名) => getTextBits(名));
		const rest = zh ? `等 ${names.length} 家` : `+${names.length - 1} more`;
		return zh ? [names[0], rest] : [...getTextBits(names[0]), rest];
	}, [currentLanguage.code, exhibitorName, exhibitors, getTextBits]);

	const textIcon = useMemo(() => {
		const width = featureWidthPixels?.replace('px', '');
		const height = featureHeightPixels?.replace('px', '');
		const numWidth = Number(width);
		const numHeight = Number(height);
		const paddingTopBot = currentZoom * 1;
		const paddingLeftRight = currentZoom * 2;
		const canvas = document.createElement('canvas');

		canvas.width = numWidth;
		canvas.height = numHeight;

		const ctx = canvas.getContext('2d');

		let boothFontSize = currentZoom * 8;
		if (numHeight / boothFontSize < 2.5) boothFontSize = numHeight / 2.5;
		if (numWidth / boothFontSize < 2.5) boothFontSize = numWidth / 3;
		ctx.globalAlpha = (100 - Number(colors.textOpacity)) / 100;
		ctx.font = `${boothFontSize}px Noto Sans TC`;
		ctx.fillText(exhibitor?.['攤位號碼'], paddingLeftRight, boothFontSize + paddingTopBot, numWidth - paddingLeftRight * 2);

		if (multi) {
			// 多家：逐行畫，字級同時受高度（行數）與寬度（最長那行）限制。
			// 英文字寬約是中文的一半，除以 0.55 讓它不要被壓得太小。
			const avaialbleHeight = numHeight - (boothFontSize + paddingTopBot * 2);
			const avaialbleWidth = numWidth - paddingLeftRight * 2;
			const zh = currentLanguage.code.includes('zh');
			const longest = Math.max(1, ...nameLines.map((行) => 行.length));
			const perChar = avaialbleWidth / (zh ? longest : longest * 0.55);
			const nameFontSize = Math.max(4, Math.min(boothFontSize, avaialbleHeight / nameLines.length, perChar));
			ctx.font = `${nameFontSize}px Noto Sans TC`;
			nameLines.forEach((text, index) => {
				ctx.fillText(text, paddingLeftRight, (boothFontSize + paddingTopBot) + nameFontSize * (index + 1), avaialbleWidth);
			});
		} else if (currentLanguage.code.includes('zh')) {
			const avaialbleHeight = numHeight - (boothFontSize + paddingTopBot * 2);
			const avaialbleWidth = numWidth - paddingLeftRight * 2;
			const characterCount = exhibitorName.length;
			const characterWidth = avaialbleWidth / characterCount;

			let nameFontSize = boothFontSize;

			if (characterWidth < avaialbleHeight) nameFontSize = characterWidth;

			ctx.font = `${nameFontSize}px Noto Sans TC`;

			ctx.fillText(exhibitorName, paddingLeftRight, (boothFontSize + paddingTopBot) + (nameFontSize), numWidth - paddingLeftRight * 2);

		} else {

			let nameFontSize = currentZoom * 8;
			if (numHeight / nameFontSize < 2.5) nameFontSize = numHeight / 2.5;

			ctx.font = `${nameFontSize}px Noto Sans TC`;

			const textBits = getTextBits();

			textBits.forEach((text, index) => {
				ctx.fillText(text, paddingLeftRight, (boothFontSize + paddingTopBot) + (nameFontSize * (index + 1)), numWidth - paddingLeftRight * 2);
			});
		}

		const dataUrl = canvas.toDataURL();
		const html = `<img src='${dataUrl}' />`;

		return divIcon({
			html,
			className: 'booth-text',
			iconSize: [Number(width), Number(height)],
		});
	}, [colors.textOpacity, currentLanguage.code, currentZoom, exhibitor, exhibitorName, featureHeightPixels, featureWidthPixels, getTextBits, multi, nameLines]);

	const Feature = useMemo(() => {
		if (shape === 'rectangle') return Rectangle;
		if (shape === 'polygon') return Polygon;
	}, [shape]);

	const isSelected = useMemo(() => {
		return boothNumbers.includes(selectedBooth);
	}, [boothNumbers, selectedBooth]);

	const isFav = useMemo(() => {
		if (!isSelected) return false;
		// 認選到的那一家（序號），不是攤位號碼——同號碼多家時只看第一家會標錯。
		const 選到的 = findExhibitorByKey(exhibitorInfo, selectedExhibitor) || (exhibitors || []).find((候選) => 候選['攤位號碼'] === selectedBooth);
		return Boolean(選到的?.favourite);
	}, [exhibitorInfo, exhibitors, isSelected, selectedBooth, selectedExhibitor]);

	const center = useMemo(() => {
		if (rectCurrent) {
			return rectCurrent.getCenter();
		}
		return [0, 0];
	}, [rectCurrent]);

	const markerIcon = useMemo(() => {
		const size = currentZoom * 20;
		return icon({
			iconUrl: isFav ? markerFav : markerSearch,
			iconSize: [size, size],
		});
	}, [currentZoom, isFav]);

	const markerCenter = useMemo(() => {
		const offset = currentZoom * 5;
		if (map) {
			const pixelCenter = map.project(center, currentZoom);
			pixelCenter.y -= offset;

			const latLngCenter = map.unproject(pixelCenter, currentZoom);
			return latLngCenter;
		}

		return [0, 0];
	}, [center, currentZoom, map]);

	// 多家時彈窗列出每一家，點哪家就開哪家，所以要吃參數；沒給就是第一家。
	const chooseExhibitor = useCallback((target) => {
		const chosen = target || exhibitor;
		if (chosen) {
			appState.value = {
				...appState.value,
				selectedBooth: chosen['攤位號碼'],
				selectedExhibitor: exhibitorKeyOf(chosen),
				ExhibitorInfo: true,
			};

			recordExhibitorClicked(chosen['攤位號碼']);
		}
	}, [exhibitor, recordExhibitorClicked]);

	const isTouchDevice = useMemo(() => {
		return 'ontouchstart' in window || navigator.maxTouchPoints;
	}, []);

	const navigateToBooth = useCallback(() => {
		makeRecord({
			Action: 'N',
			Booth: exhibitor['攤位號碼'],
		});

		appState.value = {
			...appState.value,
			selectedBooth: exhibitor['攤位號碼'],
			navigation: true,
			ExhibitorInfo: true,
		};
	}, [exhibitor, makeRecord]);

	return (
		<Feature
			ref={onRectLoad}
			bounds={bounds}
			positions={positions}
			pathOptions={{
				color: colors.border,
				fillColor: colors.fill,
				fillOpacity: (100 - Number(colors.fillOpacity)) / 100,
				opacity: (100 - Number(colors.borderOpacity)) / 100,
				weight: 3,
			}}
		>
			{/* Pane 名稱要每個圖形唯一。兩塊圖形掛同一個攤位號碼時（客戶把 F0112 的二十六個小格全部填 F0112 就會這樣）
			    react-leaflet 會丟「A pane with this name already exists」，整個地圖白掉、一個字都不顯示。
			    名稱帶上圖形自己的 id 就永遠不會撞。 */}
			{exhibitor ? (
				<Pane name={`marker${boothNumbers[0]}-${id}`} style={{ zIndex: 400 }}>
					<Marker position={center} icon={textIcon} eventHandlers={isPrint ? {} : {
						mouseover: () => {
							if (isTouchDevice) return;
							rectCurrent.fire('click');
						},
						click: () => {
							if (!isTouchDevice) return;
							rectCurrent.fire('click');
						},
					}} />
				</Pane>
			) : null}

			{/* 同上：選到的攤位那顆圖示也是一個 Pane，同號碼多塊圖形一起被選到時一樣會撞名。 */}
			{isSelected ? (
				<Pane name={`marker-selected-${id}`} style={{ zIndex: 400 }}>
					<Marker position={markerCenter} icon={markerIcon}>
					</Marker>
				</Pane>
			) : null}

			{exhibitor ? (
				<Popup>
					{multi ? (
						// 多家：只列公司名稱，簡介不顯示——客戶附件第 3 頁就是一家一張卡疊起來蓋住地圖的樣子。
						<div className='booth-info-popup multi'>
							{/* 標題放攤位號碼與家數；左上那顆導航鈕是釘在彈窗上的，跟單一家時一樣落在標題旁邊，
							    不放標題的話它會疊在第一家的名字上，看起來像只有第一家有圖示。 */}
							<div className='title'>
								{boothNumbers[0]}　{currentLanguage.code.includes('zh') ? `${exhibitors.length} 家` : `${exhibitors.length} exhibitors`}
							</div>
							{/* 名稱清單限高可捲動：6E80 那種二十幾家，一路列下去彈窗會比畫面還高、上緣被切掉。 */}
							<div className='names'>
								{exhibitors.map((候選) => (
									<div className='name' key={exhibitorKeyOf(候選)} onClick={() => { chooseExhibitor(候選) }}>
										{候選['廠商簡稱'] || 候選['廠商名稱']}
									</div>
								))}
							</div>
							{/* 清單限高 240px 大約放得下八行。超過就補一行提示——macOS 的 overlay 捲軸靜止時
							    整條隱形（連指定 ::-webkit-scrollbar 也會被忽略），使用者看不出底下還有。 */}
							{exhibitors.length > 8 ? (
								<div className='more-hint'>
									{currentLanguage.code.includes('zh') ? `往下捲看全部 ${exhibitors.length} 家` : `Scroll for all ${exhibitors.length}`}
								</div>
							) : null}
						</div>
					) : (
						<div className='booth-info-popup' onClick={() => { chooseExhibitor(exhibitor) }}>
							<div className='title'>{exhibitorName}</div>
							<div className='info-wrap'>
								<div className='details'>
									{exhibitor?.['展商基本介紹'] || ''}
								</div>
							</div>
						</div>
					)}

					<div className='navigation-control' onClick={navigateToBooth}>
						<div className='icon marker' >
						</div>
					</div>
				</Popup>
			) : null}
		</Feature>
	)
};

export default FeatureWithText;